import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Layout } from "../components/layout";
import { SEO } from "../components/SEO";
import { sanitizeHTML } from "../utils/sanitize";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DynamicPage = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [navItems, setNavItems] = useState([]);
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  // Build slug from URL params — supports /slug and /parent/child
  const slug = params["*"] || params.slug || "";

  useEffect(() => {
    axios.get(`${API}/navigation`).then((res) => setNavItems(res.data)).catch(console.error);
  }, []);

  useEffect(() => {
    const fetchPage = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/pages/${slug}`);
        if (res.data && res.data.is_published !== false) {
          setPage(res.data);
        } else {
          setPage(null);
        }
      } catch {
        setPage(null);
      }
      setLoading(false);
    };
    if (slug) fetchPage();
  }, [slug]);

  if (loading) {
    return (
      <Layout navItems={navItems}>
        <section className="section-padding bg-its-teal-dark min-h-[50vh] flex items-center justify-center">
          <div className="animate-pulse text-its-offwhite/60">Loading...</div>
        </section>
      </Layout>
    );
  }

  if (!page) {
    return (
      <Layout navItems={navItems}>
        <SEO title="Page Not Found" />
        <section className="section-padding bg-its-teal-dark min-h-[50vh] flex items-center justify-center">
          <div className="text-center" data-testid="page-not-found">
            <h1 className="heading-1 mb-4">404</h1>
            <p className="body-text mb-6">This page doesn't exist yet.</p>
            <button onClick={() => navigate("/")} className="btn-primary">
              Go Home
            </button>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout navItems={navItems}>
      <SEO
        title={page.title}
        description={page.meta_description}
        keywords={page.meta_keywords}
      />
      {/* Hero */}
      <section className="section-padding bg-its-teal-dark" data-testid="dynamic-page-hero">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="heading-1 mt-2 mb-6">{page.title}</h1>
            {page.meta_description && (
              <p className="body-text">{page.meta_description}</p>
            )}
          </motion.div>
        </div>
      </section>

      {/* Content */}
      {page.content && (
        <section className="section-padding">
          <div className="section-container">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-3xl mx-auto prose-content"
              dangerouslySetInnerHTML={{ __html: sanitizeHTML(page.content) }}
            />
          </div>
        </section>
      )}
    </Layout>
  );
};

export default DynamicPage;
